import { useRef, useState } from "react"
import { X, Upload, Check, Trash2 } from "lucide-react"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { messengerApi } from "./services/messenger.api"

interface ChatBackgroundPageProps {
  currentBackground?: string
  onClose?: () => void
  onSave?: (background: string) => void
}

// Hot-linked, so any one of them can stop resolving without a line of code
// changing here — "Flowers" had already 404'd, which is what a broken preview
// tile in this list looks like. `BackgroundThumb` below shows a label instead
// of a broken image when that happens again.
const PRESET_IMAGES = [
  { id: "img1", name: "Flowers", value: "https://images.unsplash.com/photo-1487070183336-b863922373d4?w=800&q=80" },
  { id: "img2", name: "Mountains", value: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80" },
  { id: "img3", name: "Ocean", value: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=800&q=80" },
  { id: "img4", name: "Forest", value: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80" },
  { id: "img5", name: "City", value: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80" },
  { id: "img6", name: "Abstract", value: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80" },
]

const GRADIENTS = [
  { id: "g1", name: "Twilight", value: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  { id: "g2", name: "Sunset", value: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
  { id: "g3", name: "Ocean", value: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
  { id: "g4", name: "Forest", value: "linear-gradient(135deg, #38ef7d 0%, #11998e 100%)" },
  { id: "g5", name: "Night", value: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)" },
  { id: "g6", name: "Peach", value: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)" },
]

const SOLID_COLORS = [
  { id: "s1", name: "Light Gray", value: "#f3f4f6" },
  { id: "s2", name: "Warm Beige", value: "#f5f5dc" },
  { id: "s3", name: "Soft Blue", value: "#e0f2fe" },
  { id: "s4", name: "Mint", value: "#dcfce7" },
  { id: "s5", name: "Lavender", value: "#f3e8ff" },
  { id: "s6", name: "Peach", value: "#ffe4e6" },
]

/**
 * One preset tile. Falls back to a labelled placeholder when the image cannot
 * be loaded, so a dead link reads as "this one is unavailable" rather than as a
 * rendering fault in the settings page.
 */
function BackgroundThumb({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted text-[11px] text-muted-foreground">
        {name} unavailable
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={name}
      onError={() => setFailed(true)}
      className="w-full h-full object-cover"
    />
  )
}

export function ChatBackgroundPage({ currentBackground, onClose, onSave }: ChatBackgroundPageProps) {
  const [selected, setSelected] = useState(currentBackground || "")
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  /**
   * Upload the picked image to the host's storage and keep only its URL.
   *
   * It previously became a `FileReader` data URL, which is why saving one
   * failed: the background is persisted as a bounded string on the account, and
   * a base64 image is orders of magnitude past that. Uploading also makes the
   * background work on the user's OTHER devices — a data URL only ever existed
   * in the tab that picked it.
   *
   * Same two-step the group-avatar flow uses: the S3 adapter returns a durable
   * public `url` directly; `getAttachmentUrl` is the fallback for backends that
   * don't.
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const { attachment } = await messengerApi.uploadAttachment(file)
      const url = attachment.url ?? (await messengerApi.getAttachmentUrl(attachment.id)).url
      setSelected(messengerApi.absoluteAttachmentUrl(url))
    } catch {
      toast.error({
        title: "Upload failed",
        description: "Could not upload that image. Please try again.",
      })
    } finally {
      setUploading(false)
      // Let the same file be re-picked after a failure.
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  // A custom upload is anything selected that isn't one of the built-ins. Was
  // `selected.startsWith("data:")`, which stopped matching once uploads became
  // real URLs — the preview would have silently disappeared.
  const isCustomBackground =
    !!selected &&
    !PRESET_IMAGES.some(p => p.value === selected) &&
    !GRADIENTS.some(g => g.value === selected) &&
    !SOLID_COLORS.some(c => c.value === selected)

  const handleSave = () => {
    onSave?.(selected)
    setApplied(selected)
    toast.success({
      title: "Background applied",
      description: selected ? "Your custom chat background has been set." : "Restored to default background.",
    })
    // Deliberately still here.
    //
    // Applying a background and being thrown back to the settings list makes trying a
    // second one a three-click round trip, and the toast confirming the first
    // arrives on a screen that has already gone. The X in the header is the
    // way back, and it is where it always was.
  }

  /**
   * What is actually applied, as opposed to what is highlighted. Tracked here
   * because the page stays open after Apply, and `currentBackground` is only
   * read once — so it alone could not say whether there is anything to remove.
   */
  const [applied, setApplied] = useState(currentBackground || "")

  /**
   * Remove the applied background in one step. Before this, the only way was to
   * find the "Default" tile, select it and press Apply — nothing on the page
   * said that was how to remove one.
   */
  const handleRemove = () => {
    setSelected("")
    setApplied("")
    onSave?.("")
    toast.success({ title: "Background removed", description: "Restored to default background." })
  }

  const isSelected = (val: string) => selected === val

  const PreviewCheck = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
        <Check className="w-4 h-4 text-green-600" />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-base font-semibold text-foreground">Chat Background</h2>
        <button onClick={handleSave} className="px-4 py-1.5 rounded-lg font-medium text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
          Apply
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Applied background, with a direct way to remove it */}
        {applied && (
          <div className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
            <div
              className="w-10 h-10 rounded-lg shrink-0 bg-muted bg-cover bg-center"
              style={
                applied.startsWith("#") || applied.startsWith("linear-gradient") || applied.startsWith("radial-gradient")
                  ? { background: applied }
                  : { backgroundImage: `url(${applied})` }
              }
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Current background</p>
              <p className="text-xs text-muted-foreground">Applied to all chats</p>
            </div>
            <button
              onClick={handleRemove}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
        )}

        {/* Upload custom */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Custom Image</h3>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">
                {uploading ? "Uploading…" : "Upload from device"}
              </p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WEBP</p>
            </div>
          </button>
          {isCustomBackground && (
            <div className="mt-3 relative rounded-xl overflow-hidden h-32">
              <img src={selected} alt="Custom" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Default / None */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Default</h3>
          <button
            onClick={() => setSelected("")}
            className={`relative w-full h-16 rounded-xl bg-muted overflow-hidden transition-all hover:opacity-90 flex items-center justify-center ${isSelected("") && !selected.startsWith("data:") ? "ring-2 ring-primary ring-offset-2" : ""}`}
          >
            <span className="text-sm font-medium text-muted-foreground">Default (Pattern)</span>
            {isSelected("") && !selected.startsWith("data:") && <PreviewCheck />}
          </button>
        </div>

        {/* Preset images */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Images</h3>
          <div className="grid grid-cols-2 gap-3">
            {PRESET_IMAGES.map(bg => (
              <button
                key={bg.id}
                onClick={() => setSelected(bg.value)}
                className={`relative aspect-[2/1] rounded-xl overflow-hidden transition-all hover:opacity-90 ${isSelected(bg.value) ? "ring-2 ring-primary ring-offset-2" : ""}`}
              >
                <BackgroundThumb src={bg.value} name={bg.name} />
                <span className="absolute bottom-1 left-1 right-1 text-[10px] font-medium text-white bg-black/40 rounded px-1.5 py-0.5 truncate">{bg.name}</span>
                {isSelected(bg.value) && <PreviewCheck />}
              </button>
            ))}
          </div>
        </div>

        {/* Gradients */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Gradients</h3>
          <div className="grid grid-cols-2 gap-3">
            {GRADIENTS.map(bg => (
              <button
                key={bg.id}
                onClick={() => setSelected(bg.value)}
                className={`relative aspect-[2/1] rounded-xl overflow-hidden transition-all hover:opacity-90 ${isSelected(bg.value) ? "ring-2 ring-primary ring-offset-2" : ""}`}
                style={{ background: bg.value }}
              >
                <span className="absolute bottom-1 left-1 right-1 text-[10px] font-medium text-white/90 bg-black/20 rounded px-1.5 py-0.5 truncate">{bg.name}</span>
                {isSelected(bg.value) && <PreviewCheck />}
              </button>
            ))}
          </div>
        </div>

        {/* Solid colors */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Solid Colors</h3>
          <div className="grid grid-cols-3 gap-3">
            {SOLID_COLORS.map(bg => (
              <button
                key={bg.id}
                onClick={() => setSelected(bg.value)}
                className={`relative aspect-square rounded-xl overflow-hidden transition-all hover:opacity-90 ${isSelected(bg.value) ? "ring-2 ring-primary ring-offset-2" : ""}`}
                style={{ backgroundColor: bg.value }}
              >
                <span className="absolute bottom-1 left-1 right-1 text-[10px] font-medium text-foreground/80 bg-background/80 rounded px-1.5 py-0.5 truncate text-center">{bg.name}</span>
                {isSelected(bg.value) && <PreviewCheck />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
